import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

import { Button, Card, Tag, Typography, Divider, Spin } from "antd";
import { IActivityDetail } from "@common-types";
import { ActivityDatePicker, ActivityRoomPicker } from "@share/components";
import { useDispatch, useSelector } from "react-redux";

import { activityAvailabilityActions, datesActions, getActivityDetails, roomsActions } from "@share/store/slices";
import { FormattedMessage } from "react-intl";
import { isDataValid, AppDispatch, RootState } from '@share/utils';
import { useIntl } from "react-intl";
import { WalletMessage, WalletRewardMessage } from "@components";
import { getWalletPrices, WalletPriceType } from "@utils";
import type { IAccount, IUserWallet } from "@share/common-types";

import "./style.scss";

interface IProps {
  activities?: IActivityDetail | null | undefined;
  isStatic?: boolean;
}

export const Booking: React.FC<IProps> = ({ activities, isStatic }) => {
  const { activityCode } = useParams<{ activityCode?: string }>();
  const { Title, Text } = Typography;
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const marginatorStore = useSelector((State: RootState) => State.marginatorStore);
  const loginStore = useSelector((s: RootState) => s.loginStore);
  const activityStore = useSelector((s: RootState) => s.activityStore);
  const datesStore = useSelector((s: RootState) => s.datesStore);
  const availabilityStore = useSelector((s: RootState) => s.activityAvailabilityStore);
  const activityLocationsStore = useSelector((s: RootState) => s.activityLocationsStore);

  const [loading, setLoading] = useState(false);
  const { account, userWalletData } = loginStore;
  const { selectedActivitySearchClientCash } = activityStore;

  const formatPrice = (price: number, currency?: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
    }).format(price);
  };

  useEffect(() => {
    if (!activities) return;
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [activities]);


  const percents = 100;
  const value = marginatorStore.value || 0;

  const selectedModality = availabilityStore.data?.modalities?.find((m: any) => m.code === availabilityStore.selectedModalityCode);
  const selectedRate = selectedModality?.rates?.find((r: any) => r.rateKey === availabilityStore.selectedRateKey);

  const service = selectedRate || activities?.modalities?.[0]?.rates?.[0];
 
  
  const getMinPublicPrice = useCallback((): number => {
    const publicPrices = service?.publicPrices;
    if (publicPrices && publicPrices.length) {
      return Math.min(...publicPrices.map(({ price }) => price));
    }
    return 200;
  }, [selectedRate]);

  let pubPrice = 0;
  if (!isStatic) {
    pubPrice = getMinPublicPrice();
  }

  const marginatorPrice = (service?.price || 0) / (1 - value / 100);
  const marginatorSavings = (service?.saving || 0) / (1 - value / 100);
  const msave = pubPrice
    ? account?.breakdownTaxes
      ? (1 - marginatorSavings / pubPrice) * percents
      : (marginatorPrice * percents) / pubPrice
    : 100;
  const marginatorsavePercents = Math.floor(percents - msave);

  const maxWalletClientCash = service?.maxWalletClientCash ?? 0;
  const maxRewardWalletAmount = (service as { maxRewardWalletAmount?: number })?.maxRewardWalletAmount;
  const hotelPrices = getWalletPrices(
    account as IAccount,
    userWalletData as IUserWallet,
    selectedActivitySearchClientCash,
    marginatorPrice,
    maxWalletClientCash,
    WalletPriceType.Details
  );
  const displayWalletSavings =
    !!account?.walletWalletSavings &&
    (!!(hotelPrices?.priceSavings && hotelPrices.priceSavings > 0) || !!(hotelPrices?.maxClientCashAmountAllow && hotelPrices.maxClientCashAmountAllow > 0)) &&
    !!hotelPrices?.maxClientCashAmountAllow;

  const handleAvailability = async () => {
    const { isDatesValid, isDatesMaxError } = isDataValid(activityLocationsStore, datesStore);
    if (!isDatesValid) {
      dispatch(
        datesActions.setError(
          intl.formatMessage({
            id: isDatesMaxError ? "maximum.date.range.search" : "dates.error",
          })
        )
      );
      return;
    }
    dispatch(datesActions.applyDates());
    dispatch(roomsActions.applyRooms());
    const code = activities?.code || activityCode;
    if (code) {
      await dispatch(getActivityDetails({ ActivityCode: code, isStatic: false, sessionKey: undefined }));
    }
    dispatch(activityAvailabilityActions.checkAvailability({ ActivityCode: code }));
  };

  return (
    <div className="booking-sidebar">
      <Card className="booking-card">
        
        <div className="price-section">
          {(!isStatic || pubPrice > 0) && marginatorsavePercents > 0 && (
            <div className="original-price">
              <Text delete>
                {formatPrice(
                  pubPrice,
                  activities?.currency
                )}{" "}
                < FormattedMessage id="activity.details.usd" defaultMessage="USD" />
              </Text>
            </div>
          )}
          <div className="current-price">
            {(!isStatic || marginatorPrice > 0) && (
              <Title level={2}>
                {formatPrice(marginatorPrice || 0, activities?.currency)} <FormattedMessage id="activity.details.usd" defaultMessage="USD" />
              </Title>
            )}
          </div>
          {marginatorsavePercents > 0 && (
            <Tag color="red" className="savings-tag">
              <FormattedMessage id="activity.details.save" defaultMessage="Save" /> {marginatorsavePercents}%
            </Tag>
          )}
        </div>

        {!account?.isMLM && (!isStatic || displayWalletSavings) ? (
          <>
            <WalletMessage
              price={marginatorPrice}
              maxClientCash={maxWalletClientCash}
              currency={activities?.currency}
              type={WalletPriceType.Details}
              isOneLine
              hideApplied={displayWalletSavings}
              greenText={displayWalletSavings}
              style={{ paddingLeft: "15px", paddingRight: "15px", marginBottom: "15px" }}
            />
            <WalletRewardMessage
              price={marginatorPrice}
              maxRewardCash={maxRewardWalletAmount}
              currency={activities?.currency}
              type={WalletPriceType.Details}
            />
          </>
        ) : null}
        {!isStatic && (
        <Divider />
        )}
        {/* Date + Guest Selection */}
        <div className="bookingDetails selection-section">
          <div className={`date-selection ${availabilityStore?.error ? 'date-selection-error' : ''}`}>
            <ActivityDatePicker  />
            <ActivityRoomPicker ageRange={activities?.ageRange} ageBands={activities?.ageBands} />
          </div>

          {availabilityStore?.error && (
            <div className="activity-error-message">
              <FormattedMessage id={availabilityStore?.error} defaultMessage={availabilityStore?.errorMessages} />
            </div>)}
        </div>

        <Divider />

        {/* Total Section */}
        {!isStatic && (
        <div className="total-section">
          <div className="total-row">
            <Text strong><FormattedMessage id="activity.details.total" defaultMessage="Total" />:</Text>
            <Text strong className="total-price">
              {loading ? (
                <Spin />
              ) : (
                formatPrice(marginatorPrice, activities?.currency)
              )}
            </Text>
          </div>
        </div>
        )}
        {/* Select Activity Button */}
        <Button
          type="primary"
          size="large"
          loading={availabilityStore.loading}
          disabled={loading || availabilityStore.loading}
          className="select-activity-btn"
          block
          onClick={() => handleAvailability()}
        >
          <FormattedMessage id="activity.check.availability" />
        </Button>
      </Card>
    </div>
  );
};
